import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Flame, Lock, Clock, TrendingDown, Coins, BarChart3, Shield, Zap, ChevronDown, ChevronUp, ExternalLink, RefreshCw, Activity, Crown, ArrowUpRight, Repeat, Wallet, Copy, Check, Calculator } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

const TOTAL_SUPPLY = 1_000_000_000;
const INITIAL_SUPPLY = 100_000_000;

const distribution = [
  { label: 'Public Sale', pct: 30, color: '#3B82F6', tokens: 300_000_000, description: 'Available through IDO and DEX launch on Base' },
  { label: 'Staking Allocation', pct: 20, color: '#8B5CF6', tokens: 200_000_000, description: 'Allocated to stakers for governance participation and protocol access' },
  { label: 'Team & Advisors', pct: 15, color: '#F59E0B', tokens: 150_000_000, description: '12-month cliff, then 24-month linear vesting' },
  { label: 'Treasury', pct: 15, color: '#10B981', tokens: 150_000_000, description: 'DAO-governed for protocol development & grants' },
  { label: 'Ecosystem', pct: 10, color: '#EC4899', tokens: 100_000_000, description: 'Partnerships, integrations, and community incentives' },
  { label: 'Liquidity', pct: 10, color: '#06B6D4', tokens: 100_000_000, description: 'DEX liquidity pools and market-making on Base' },
];

const vestingSchedule = [
  { category: 'Public Sale', cliff: 'None', vesting: 'Fully unlocked at TGE', tge: '100%' },
  { category: 'Staking Rewards', cliff: 'None', vesting: 'Emitted over 5 years via smart contract', tge: '0%' },
  { category: 'Team & Advisors', cliff: '12 months', vesting: '24-month linear after cliff', tge: '0%' },
  { category: 'Treasury', cliff: '6 months', vesting: 'DAO vote required for each release', tge: '0%' },
  { category: 'Ecosystem', cliff: 'None', vesting: '36-month linear', tge: '5%' },
  { category: 'Liquidity', cliff: 'None', vesting: 'Locked for 12 months, then DAO-managed', tge: '50%' },
];

const burnMechanics = [
  { trigger: 'Credit Origination Fee', rate: '0.5% of credit draw', icon: Coins, description: 'Every credit draw burns a portion of the platform fee collected in KOJIRU' },
  { trigger: 'Staking Early Withdrawal', rate: '2% penalty burned', icon: Lock, description: 'Unstaking before the 7-day minimum triggers a deflationary burn' },
  { trigger: 'Governance Proposal Fee', rate: '10 KOJIRU per proposal', icon: Shield, description: 'Creating governance proposals requires a small burn to prevent spam' },
  { trigger: 'Quarterly Buy & Burn', rate: 'Up to 5% of revenue', icon: Flame, description: 'Protocol revenue used to buy KOJIRU from the market and permanently burn it' },
];

function fmt(n) {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(0) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(0) + 'K';
  return n.toString();
}

function DonutChart({ liveSupply }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="relative w-64 h-64 mx-auto">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        {distribution.map((seg, i) => {
          const dash = (seg.pct / 100) * circumference;
          const gap = circumference - dash;
          const el = (
            <circle
              key={i}
              cx="50" cy="50" r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth="18"
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              className="transition-all duration-500"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold">{liveSupply ? fmt(parseFloat(liveSupply)) : fmt(TOTAL_SUPPLY)}</span>
        <span className="text-sm text-gray-400">{liveSupply ? 'Circulating' : 'Max Supply'}</span>
      </div>
    </div>
  );
}

export default function Tokenomics() {
  const [expandedBurn, setExpandedBurn] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [holdersData, setHoldersData] = useState(null);
  const [holdersLoading, setHoldersLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Staking Rewards Calculator state
  const [calcAmount, setCalcAmount] = useState(10000); // KOJIRU to stake
  const [calcMonths, setCalcMonths] = useState(12); // Duration in months

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchLiveData = async () => {
    setLiveLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/token/stats`);
      if (res.ok) {
        const data = await res.json();
        setLiveData(data);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch live token data:', err);
    } finally {
      setLiveLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveData();
    fetchHolders();
    const interval = setInterval(fetchLiveData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchHolders = async () => {
    setHoldersLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/token/holders`);
      if (res.ok) {
        const data = await res.json();
        setHoldersData(data);
      }
    } catch (err) {
      console.error('Failed to fetch holders:', err);
    } finally {
      setHoldersLoading(false);
    }
  };

  // Staking Rewards Calculator
  const STAKING_APY = 10; // 10% APY from smart contract
  const EARLY_WITHDRAWAL_PENALTY = 2; // 2% burn
  const MIN_STAKE_DAYS = 7;

  const calcRewards = calcAmount * (STAKING_APY / 100) * (calcMonths / 12);
  const calcTotal = calcAmount + calcRewards;
  const calcEarlyPenalty = calcAmount * (EARLY_WITHDRAWAL_PENALTY / 100);

  // What tier does this stake unlock?
  const calcTier = calcAmount >= 100000 ? { name: 'Sovereign', color: 'text-yellow-400', credit: '$100,000', acs: '750-850' }
    : calcAmount >= 25000 ? { name: 'Autonomous', color: 'text-purple-400', credit: '$25,000', acs: '650-749' }
    : calcAmount >= 5000 ? { name: 'Supervised', color: 'text-blue-400', credit: '$5,000', acs: '550-649' }
    : calcAmount >= 1000 ? { name: 'Provisional', color: 'text-gray-400', credit: '$1,000', acs: '450-549' }
    : { name: 'Below minimum', color: 'text-red-400', credit: '$0', acs: '< 450' };

  // Monthly reward breakdown for chart
  const calcMonthly = Array.from({ length: Math.min(calcMonths, 12) }, (_, i) => {
    const m = i + 1;
    const earned = calcAmount * (STAKING_APY / 100) * (m / 12);
    return { month: m, earned, total: calcAmount + earned };
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-blue-400 mb-6">
              <Coins className="w-4 h-4" />
              ERC-20 on Base
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              KOJIRU <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Tokenomics</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-testid="tokenomics-subtitle">
              A deflationary utility token powering the Agent Credit Protocol on Base — built for long-term value accrual.
            </p>
          </motion.div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {[
              { label: 'Max Supply', value: fmt(TOTAL_SUPPLY), icon: BarChart3 },
              { label: 'Initial Circulating', value: fmt(INITIAL_SUPPLY), icon: Coins },
              { label: 'Staking Rate', value: 'Variable', icon: TrendingDown },
              { label: 'Burn Mechanism', value: 'Deflationary', icon: Flame },
            ].map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}
                className="bg-white/5 rounded-2xl p-5 border border-white/10"
              >
                <m.icon className="w-6 h-6 text-blue-500 mb-3" />
                <div className="text-2xl font-bold mb-1">{m.value}</div>
                <div className="text-sm text-gray-400">{m.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Burn Tracker */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="burn-tracker"
            className="relative overflow-hidden rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/5 via-[#0a0a0a] to-red-500/5"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Live Token Tracker</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    Reading from Base Mainnet
                    {lastRefresh && <span>&middot; Updated {lastRefresh.toLocaleTimeString()}</span>}
                  </div>
                </div>
              </div>
              <button
                onClick={fetchLiveData}
                disabled={liveLoading}
                data-testid="refresh-token-stats"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${liveLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
              {[
                {
                  label: 'Circulating Supply',
                  value: liveData ? fmt(parseFloat(liveData.total_supply)) : '—',
                  sub: liveData ? `${liveData.circulating_pct}% of max` : '',
                  color: 'text-blue-400'
                },
                {
                  label: 'Total Burned',
                  value: liveData ? fmt(parseFloat(liveData.total_burned)) : '—',
                  sub: liveData ? `${liveData.burned_pct}% of max` : '',
                  color: 'text-orange-400'
                },
                {
                  label: 'Remaining Mintable',
                  value: liveData ? fmt(parseFloat(liveData.remaining_mintable)) : '—',
                  sub: liveData ? `${(100 - liveData.circulating_pct).toFixed(1)}% of max` : '',
                  color: 'text-purple-400'
                },
                {
                  label: 'Max Supply',
                  value: liveData ? fmt(parseFloat(liveData.max_supply)) : '1.0B',
                  sub: 'Hard cap',
                  color: 'text-white'
                }
              ].map((stat, i) => (
                <div key={i} data-testid={`live-stat-${i}`} className="bg-[#0a0a0a] p-5">
                  <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">{stat.label}</div>
                  <div className={`text-2xl md:text-3xl font-bold ${stat.color}`}>
                    {liveLoading && !liveData ? (
                      <div className="h-8 w-24 bg-white/5 rounded animate-pulse" />
                    ) : (
                      stat.value
                    )}
                  </div>
                  {stat.sub && <div className="text-xs text-gray-500 mt-1">{stat.sub}</div>}
                </div>
              ))}
            </div>

            {/* Supply Bar */}
            {liveData && (
              <div className="p-6">
                <div className="text-sm text-gray-400 mb-2">Supply Distribution</div>
                <div className="h-4 rounded-full overflow-hidden flex bg-white/5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${liveData.circulating_pct}%` }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-l-full"
                    title={`Circulating: ${liveData.circulating_pct}%`}
                  />
                  {parseFloat(liveData.total_burned) > 0 && (
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${liveData.burned_pct}%` }}
                      transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
                      className="bg-gradient-to-r from-orange-500 to-red-500"
                      title={`Burned: ${liveData.burned_pct}%`}
                    />
                  )}
                </div>
                <div className="flex items-center gap-6 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Circulating ({liveData.circulating_pct}%)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> Burned ({liveData.burned_pct}%)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-white/10" /> Unminted ({(100 - liveData.circulating_pct - liveData.burned_pct).toFixed(1)}%)
                  </span>
                </div>

                {/* Contract Link */}
                <span
                  className="inline-flex items-center gap-1.5 mt-4 text-xs text-gray-500"
                >
                  Deployed & Verified on Base Mainnet
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Distribution */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mb-12"
          >
            Token <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Distribution</span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <DonutChart liveSupply={liveData?.total_supply} />
            </motion.div>

            <div className="space-y-3">
              {distribution.map((seg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                  data-testid={`dist-${seg.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors"
                >
                  <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{seg.label}</span>
                      <span className="font-bold text-lg">{seg.pct}%</span>
                    </div>
                    <div className="text-sm text-gray-400 mt-0.5">{seg.description}</div>
                  </div>
                  <span className="text-sm text-gray-500 shrink-0">{fmt(seg.tokens)}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vesting Schedule */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Vesting <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Schedule</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Transparent unlock timeline designed to align long-term incentives.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="vesting-table">
                <thead className="border-b border-white/10">
                  <tr className="text-left text-gray-400 text-sm">
                    <th className="px-6 py-4 font-medium">Category</th>
                    <th className="px-6 py-4 font-medium">TGE Unlock</th>
                    <th className="px-6 py-4 font-medium">Cliff Period</th>
                    <th className="px-6 py-4 font-medium">Vesting Details</th>
                  </tr>
                </thead>
                <tbody>
                  {vestingSchedule.map((row, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 font-medium">{row.category}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          row.tge === '100%' ? 'bg-green-500/20 text-green-400' :
                          row.tge === '0%' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {row.tge}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300">{row.cliff}</td>
                      <td className="px-6 py-4 text-gray-400 text-sm">{row.vesting}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Burn Mechanics */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Burn <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">Mechanics</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Multiple deflationary mechanisms reduce supply over time, increasing scarcity and long-term value.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {burnMechanics.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                data-testid={`burn-${i}`}
                className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden cursor-pointer hover:border-orange-500/30 transition-colors"
                onClick={() => setExpandedBurn(expandedBurn === i ? null : i)}
              >
                <div className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{item.trigger}</div>
                    <div className="text-sm text-orange-400 font-medium">{item.rate}</div>
                  </div>
                  {expandedBurn === i ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
                  )}
                </div>
                {expandedBurn === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="px-5 pb-5 text-sm text-gray-400"
                  >
                    {item.description}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Supply Projection */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 rounded-2xl border border-white/10 p-8"
          >
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <TrendingDown className="w-6 h-6 text-green-500" />
              Projected Supply Reduction
            </h3>
            <div className="space-y-4">
              {[
                { year: 'Year 1', supply: '950M', burned: '50M', pct: 95 },
                { year: 'Year 2', supply: '880M', burned: '120M', pct: 88 },
                { year: 'Year 3', supply: '790M', burned: '210M', pct: 79 },
                { year: 'Year 5', supply: '620M', burned: '380M', pct: 62 },
              ].map((row, i) => (
                <div key={i} data-testid={`projection-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">{row.year}</span>
                    <span className="text-gray-400">
                      {row.supply} supply &middot; <span className="text-orange-400">{row.burned} burned</span>
                    </span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: '100%' }}
                      whileInView={{ width: `${row.pct}%` }}
                      transition={{ duration: 1, delay: i * 0.2 }}
                      viewport={{ once: true }}
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
              <div className="flex items-start gap-3">
                <Flame className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-orange-400 mb-1">Deflationary by Design</div>
                  <p className="text-sm text-gray-400">
                    With no new token minting after the initial supply, and multiple burn mechanisms active from day one, KOJIRU's circulating supply is designed to decrease over time — creating natural scarcity as platform adoption grows.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Staking Rewards Calculator */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Staking Rewards <span className="bg-gradient-to-r from-purple-400 to-green-400 bg-clip-text text-transparent">Calculator</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Estimate your KOJIRU staking rewards and see which Agent Credit tier your stake unlocks.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="staking-calculator"
            className="rounded-2xl border border-purple-500/20 overflow-hidden"
          >
            <div className="grid md:grid-cols-5 gap-px bg-white/5">
              {/* Controls — left 3 cols */}
              <div className="md:col-span-3 bg-[#0a0a0a] p-6 md:p-8 space-y-8">
                <div className="flex items-center gap-3 mb-2">
                  <Calculator className="w-5 h-5 text-purple-400" />
                  <h3 className="font-semibold text-lg">Configure Your Stake</h3>
                </div>

                {/* Amount to Stake */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm text-gray-400">KOJIRU to Stake</label>
                    <span className="text-sm font-bold text-purple-400" data-testid="calc-amount-value">{fmt(calcAmount)} KOJIRU</span>
                  </div>
                  <Slider
                    data-testid="calc-amount-slider"
                    value={[calcAmount]}
                    onValueChange={([v]) => setCalcAmount(v)}
                    min={100}
                    max={500000}
                    step={100}
                    className="[&_[role=slider]]:bg-purple-500 [&_[role=slider]]:border-purple-400"
                  />
                  <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                    <span>100</span>
                    <span>500,000</span>
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm text-gray-400">Staking Duration</label>
                    <span className="text-sm font-bold text-blue-400" data-testid="calc-months-value">{calcMonths} month{calcMonths > 1 ? 's' : ''}</span>
                  </div>
                  <Slider
                    data-testid="calc-months-slider"
                    value={[calcMonths]}
                    onValueChange={([v]) => setCalcMonths(v)}
                    min={1}
                    max={60}
                    step={1}
                    className="[&_[role=slider]]:bg-blue-500 [&_[role=slider]]:border-blue-400"
                  />
                  <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                    <span>1 month</span>
                    <span>60 months</span>
                  </div>
                </div>

                {/* Reward Growth Chart */}
                <div>
                  <div className="text-xs text-gray-500 mb-3">Monthly Reward Accumulation</div>
                  <div className="flex items-end gap-1 h-20">
                    {calcMonthly.map((p, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: `${Math.max((p.earned / calcRewards) * 100, 4)}%` }}
                          transition={{ duration: 0.5, delay: i * 0.05 }}
                          data-testid={`calc-bar-${i}`}
                          className="w-full rounded-t bg-gradient-to-t from-green-500/60 to-green-400 min-h-[4px]"
                        />
                        {(i === 0 || i === calcMonthly.length - 1 || i === Math.floor(calcMonthly.length / 2)) && (
                          <span className="text-[8px] text-gray-600">M{p.month}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Protocol Constants */}
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/5">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-400">{STAKING_APY}%</div>
                    <div className="text-[10px] text-gray-500">Current Rate*</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-400">{EARLY_WITHDRAWAL_PENALTY}%</div>
                    <div className="text-[10px] text-gray-500">Early Exit Burn</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-400">{MIN_STAKE_DAYS}d</div>
                    <div className="text-[10px] text-gray-500">Minimum Lock</div>
                  </div>
                </div>
              </div>

              {/* Results — right 2 cols */}
              <div className="md:col-span-2 bg-[#080808] p-6 md:p-8 flex flex-col">
                {/* Primary Result */}
                <div className="text-center mb-6">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Total Rewards Earned</div>
                  <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent" data-testid="calc-rewards-result">
                    +{fmt(Math.round(calcRewards))}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">KOJIRU over {calcMonths} month{calcMonths > 1 ? 's' : ''}</div>
                </div>

                {/* Credit Tier Unlocked */}
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-6">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Agent Credit Tier Unlocked</div>
                  <div className={`text-2xl font-bold ${calcTier.color}`} data-testid="calc-tier-result">
                    {calcTier.name}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Max credit line: <span className="text-white font-medium">{calcTier.credit}</span>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="space-y-3 border-t border-white/5 pt-5 flex-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Staked</span>
                    <span className="font-medium" data-testid="calc-staked">{fmt(calcAmount)} KOJIRU</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Rewards</span>
                    <span className="font-medium text-green-400" data-testid="calc-earned">+{fmt(Math.round(calcRewards))} KOJIRU</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total at End</span>
                    <span className="font-bold" data-testid="calc-total">{fmt(Math.round(calcTotal))} KOJIRU</span>
                  </div>
                  <div className="flex justify-between text-sm pt-3 border-t border-white/5">
                    <span className="text-gray-500">Monthly Reward</span>
                    <span className="font-medium">{fmt(Math.round(calcAmount * (STAKING_APY / 100) / 12))} KOJIRU</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Early Exit Penalty</span>
                    <span className="font-medium text-orange-400">{fmt(Math.round(calcEarlyPenalty))} KOJIRU burned</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-[#060606] px-6 py-3 border-t border-white/5">
              <p className="text-[10px] text-gray-600 text-center">
                *Staking rate is set by the Kojiru Staking V2 smart contract on Base Mainnet and may vary. Staking tokens does not guarantee returns and token value may fluctuate. Early unstaking (before {MIN_STAKE_DAYS} days) incurs a {EARLY_WITHDRAWAL_PENALTY}% burn penalty. The KOJIRU token is a utility token for governance and protocol access — it is not a security or investment product. Not registered with the SEC.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Token Holders Leaderboard */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Top <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">Holders</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Live leaderboard of the largest KOJIRU token holders on Base.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="holders-leaderboard"
            className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-yellow-500" />
                <span className="font-semibold">Holder Leaderboard</span>
                {holdersData && (
                  <span className="text-xs bg-white/5 px-2 py-1 rounded-full text-gray-400">
                    {holdersData.total_holders} holder{holdersData.total_holders !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <button
                onClick={fetchHolders}
                disabled={holdersLoading}
                data-testid="refresh-holders"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${holdersLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Table */}
            {holdersLoading && !holdersData ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-gray-500" />
                <p className="text-sm text-gray-500">Loading holders...</p>
              </div>
            ) : holdersData?.holders?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-white/5">
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3">Rank</th>
                      <th className="px-5 py-3">Address</th>
                      <th className="px-5 py-3 text-right">Balance</th>
                      <th className="px-5 py-3 text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdersData.holders.map((holder, i) => (
                      <tr key={i} data-testid={`holder-row-${i}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-5 py-4">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                            i === 1 ? 'bg-gray-400/20 text-gray-300' :
                            i === 2 ? 'bg-orange-600/20 text-orange-400' :
                            'bg-white/5 text-gray-400'
                          }`}>
                            {i + 1}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="font-mono text-sm text-gray-400">
                            {holder.short_address}
                          </span>
                          {holder.is_contract && (
                            <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded ml-2">Contract</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right font-medium">{holder.balance} KOJI</td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 rounded-full bg-white/5 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full"
                                style={{ width: `${Math.min(holder.percentage, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-400 w-14 text-right">{holder.percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">
                <p>No holder data available yet.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Coinbase Exchange Announcement */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="coinbase-announcement"
            className="relative overflow-hidden rounded-2xl border border-blue-500/20"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-[#080808] to-blue-400/5" />
            <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500/8 rounded-full blur-[100px]" />

            <div className="relative z-10 p-8 md:p-10">
              {/* Header */}
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <span className="text-xl font-bold text-blue-400">C</span>
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h3 className="text-2xl font-bold">Now Tradable on Coinbase</h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-green-500/15 border border-green-500/20 text-green-400 text-xs font-semibold uppercase tracking-wider">Live</span>
                  </div>
                  <p className="text-gray-400 text-sm max-w-2xl">
                    As part of Coinbase's "Everything Exchange" initiative, every token launched on the Base network is now tradable within the Coinbase ecosystem — including KOJIRU (KOJI).
                  </p>
                </div>
              </div>

              {/* Trading Pairs */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Available Pairs</div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center text-xs font-bold text-blue-400">ETH</div>
                        <span className="font-medium">KOJI / ETH</span>
                      </div>
                      <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">Active</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center text-xs font-bold text-blue-400">USDC</div>
                        <span className="font-medium">KOJI / USDC</span>
                      </div>
                      <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">Active</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">About This Initiative</div>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    In mid-2025, Coinbase CEO Brian Armstrong announced unprecedented access to Base tokens as part of the "Everything Exchange" roadmap for 2026 — allowing users to trade novel cryptocurrencies, including micro-cap tokens on Base, alongside traditional assets in one unified interface.
                  </p>
                </div>
              </div>

              {/* Footer Link */}
              <div className="flex items-center justify-between flex-wrap gap-4 pt-5 border-t border-white/5">
                <p className="text-xs text-gray-500">
                  Coinbase has expanded USDC settlement to over 230 trading pairs, enabling direct trading on USD order books with USDC.
                </p>
                <a
                  href="https://www.coinbase.com/blog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/20 transition-colors"
                  data-testid="coinbase-blog-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Read Coinbase Announcement
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Buy KOJIRU CTA */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            data-testid="buy-kojiru-cta"
            className="relative overflow-hidden rounded-3xl border border-blue-500/20"
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-[#080808] to-purple-500/10" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[100px]" />

            <div className="relative z-10 p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-10 items-center">
                {/* Left: Info */}
                <div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-xs text-blue-400 mb-5">
                    <Coins className="w-3.5 h-3.5" />
                    Available on Base
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    Get <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">KOJIRU</span> Tokens
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Acquire KOJIRU tokens to stake for AI agent access, participate in governance, and contribute to protocol security. Staking parameters are set by the smart contract and may vary.
                  </p>

                  {/* Contract Address */}
                  <div className="mb-6">
                    <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Contract</div>
                    <div className="flex items-center gap-2 bg-white/5 rounded-xl p-3 border border-white/10">
                      <span className="text-sm text-gray-400">Deployed on Base Mainnet. Address available at official launch.</span>
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div className="flex flex-wrap gap-3">
                    <span
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-gray-500"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Explorer — Coming Soon
                    </span>
                  </div>
                </div>

                {/* Right: Swap Options */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-blue-400" />
                    Swap on DEX
                  </h3>

                  {[
                    {
                      name: 'Uniswap',
                      desc: 'The leading decentralized exchange',
                      color: 'from-pink-500/20 to-purple-500/20',
                      border: 'border-pink-500/20'
                    },
                    {
                      name: 'Aerodrome',
                      desc: "Base's native liquidity hub",
                      color: 'from-blue-500/20 to-cyan-500/20',
                      border: 'border-blue-500/20'
                    },
                    {
                      name: 'BaseSwap',
                      desc: 'Popular DEX on Base',
                      color: 'from-green-500/20 to-teal-500/20',
                      border: 'border-green-500/20'
                    }
                  ].map((dex, i) => (
                    <div
                      key={i}
                      data-testid={`dex-${dex.name.toLowerCase()}`}
                      className={`flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r ${dex.color} border ${dex.border} opacity-60`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-lg font-bold shrink-0">
                        {dex.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{dex.name}</div>
                        <div className="text-xs text-gray-400">{dex.desc}</div>
                      </div>
                      <span className="text-[10px] text-gray-500 shrink-0">Coming Soon</span>
                    </div>
                  ))}

                  <p className="text-[11px] text-gray-600 mt-3">
                    DEX listings and liquidity pools will be available at official launch.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
